import React, {useEffect, useRef, useState} from 'react';
import {useRpcSender} from "../../RpcSender";
import {
  FindProviderRequest,
  FindProviderResponse,
  IProviderSearchResult,
  IPublicBusinessProfileProto,
  space,
} from "../../compiled";
import {useApiIsLoaded, useMap, useMapsLibrary} from '@vis.gl/react-google-maps';
import './MapSearch.css'
import {SearchResult} from "./SearchResult";
import {MapMarkerPopup} from "./MapMarkerPopup";
import IBusinessLocationProto = space.kenko.proto.IBusinessLocationProto;
import IBusinessProfileProto = space.kenko.proto.IBusinessProfileProto;

interface Props {

  defaultLocation: google.maps.LatLngLiteral

  serviceCategory: string | undefined
}

/**
 * @constructor
 */
export const ProviderSearchResultsList = (props: Props) => {

  const rpcSender = useRpcSender();

  const [results, setResults] = useState<Map<string, IProviderSearchResult>>(
      new Map<string, IProviderSearchResult>());

  const [rpcLoading, setRpcLoading] = useState(true);
  const [rpcError, setRpcError] = useState('');

  // NOTE - map bounds are definitely NOT state because it causes a zillion re-renders
  const initialLoadDone = useRef(false);

  const map = useMap()

  const apiLoaded = useApiIsLoaded();
  const markerLibrary = useMapsLibrary('marker');

  const infoWindow = useRef(new google.maps.InfoWindow({
    content: 'hmmm',
    ariaLabel: "hummmm",
    headerDisabled: true,
    // minWidth: 300,
  }));

  // All POI markers currently out on the map. Since these are map elements so they are
  // persisted across renders. This is a dictionary so we can do a fast lookup by id
  // and keep any markers that already exist. This eliminates what would otherwise be
  // a discernible flicker in the markers.
  const markers = useRef<Map<string, google.maps.marker.AdvancedMarkerElement>>(
      new Map<string, google.maps.marker.AdvancedMarkerElement>());

  const boundsListenerRef = useRef<google.maps.MapsEventListener>();
  const dragListenerRef = useRef<google.maps.MapsEventListener>();
  const zoomListenerRef = useRef<google.maps.MapsEventListener>();
  const clickListenerRef = useRef<google.maps.MapsEventListener>();


  useEffect(() => {
    if (map) {

      // Awkward conversation about closures... if the service category is not included
      // in the dependency list, closures are created for the updateSearchResults and the
      // service category never actually got reflected in the RPC that was sent out. So
      // I've added clean uppable listeners and refs and a useEffecy dependency on the category,

      boundsListenerRef.current = map.addListener('bounds_changed', () => {
        // This listener sets the INITIAL bounds which are determined once the map initializes.
        if (!initialLoadDone.current) {
          console.log('initial load done', initialLoadDone)
          initialLoadDone.current = true;
          updateSearchResults();
        }
      });

      dragListenerRef.current = map.addListener('dragend', updateSearchResults)
      zoomListenerRef.current = map.addListener('zoom_changed', updateSearchResults)
      clickListenerRef.current = map.addListener('click', closeInfoWindow);

      // Cleanup function to remove the listener
      return () => {
        boundsListenerRef.current && window.google.maps.event.removeListener(boundsListenerRef.current);
        dragListenerRef.current && window.google.maps.event.removeListener(dragListenerRef.current);
        zoomListenerRef.current && window.google.maps.event.removeListener(zoomListenerRef.current);
      };

    }
  }, [map, props.serviceCategory])

  useEffect(() => {
    if (map) {
      updateSearchResults();
    }
  }, [props.serviceCategory, props.defaultLocation])

  const closeInfoWindow = () => {
    infoWindow?.current.close();
  }

  const updateSearchResults = () => {

    let mapBounds = map?.getBounds();
    if (!mapBounds) {
      console.log('map bounds not available yet');
      return;
    }

    setRpcLoading(true);
    setRpcError('');

    let request = new FindProviderRequest({
      category: props.serviceCategory,
      north: mapBounds.getNorthEast().lat(),
      east: mapBounds.getNorthEast().lng(),
      south: mapBounds.getSouthWest().lat(),
      west: mapBounds.getSouthWest().lng(),
    });


    console.log('sending request', request);

    rpcSender.send("find_provider", FindProviderRequest.encode(request).finish())
        .then(r => {
              let response = FindProviderResponse.decode(r);

              if (response.viewportTooLarge) {
                setRpcError('Map viewport is too large - try zooming in!')
              }

              let updatedResults: Map<string, IProviderSearchResult> = new Map<string, IProviderSearchResult>();
              response.providers.forEach(provider => {
                if (provider.businessId) {
                  updatedResults.set(provider.businessId, provider);
                }
              });

              setResults(updatedResults);

              // Clear any markers no longer in the search results.
              markers.current.forEach((marker, id) => {
                if (!updatedResults.has(id)) {
                  marker.map = null;
                  markers.current.delete(id); // Supposedly deleting while iterating is ok...
                }
              });

              // Add markers for ones still there.
              updatedResults?.forEach(provider => {

                const markerId = provider.location?.locationId as string;

                if (!markers.current.has(markerId)) {

                  let physicalLocation = provider.location?.physicalLocation;
                  if (!physicalLocation || !physicalLocation?.lat || !physicalLocation?.lng) {
                    // Shouldn't happen but this check makes typescript happy.
                    return;
                  }

                  // Not existent, make a new one.
                  let marker = new google.maps.marker.AdvancedMarkerElement({
                    position: {lat: physicalLocation.lat, lng: physicalLocation.lng},
                    title: provider?.businessProfile?.businessName,
                    map: map,
                    gmpClickable: true,
                    content: new google.maps.marker.PinElement({
                      background: 'var(--orange)',
                      glyphColor: 'white',
                      borderColor: 'black',
                    }).element
                  });

                  // Add a click listener for each marker, and set up the info window.
                  marker.addListener('click', () => {

                    infoWindow.current.close();
                    infoWindow.current.setContent(MapMarkerPopup({
                      searchResult: provider,
                    }));
                    infoWindow.current.open(marker.map, marker);
                  });

                  markers.current.set(markerId, marker);
                }
              })
            }
        )
        .catch(e => {
          console.log(e)
          setRpcError('Error searching, so sorry!')
        })
        .finally(() => {
          setRpcLoading(false);
        })
  };

  if (!apiLoaded) {
    return <div>Loading maps api...</div>
  }

  if (!markerLibrary) {
    return <div>Loading marker library...</div>
  }

  if (rpcError) {
    return (<div className={'ErrorText'}>{rpcError}</div>);
  }

  if (results.size == 0) {
    if (rpcLoading) {
      return <div style={{padding: 20}}>Loading...</div>
    } else {
      return <div style={{padding: 20}}>No providers found, sorry!</div>
    }
  }

  /**
   *
   {props.pois?.map((poi: Poi) => (
   <AdvancedMarker key={poi.key} position={poi.location}>
   <Pin background={'gray'} glyphColor={'#fff'} borderColor={'black'}/>
   </AdvancedMarker>
   ))}
   */


// The left side of the search results. Interacts directly with the map so it doesn't
// cause a re-render.
  return <div className={'MapSearchLocations'}>
    {Array.from(results.values()).map(result => {
      return <SearchResult key={result.location?.locationId}
                           businessId={result.businessId as string}
                           businessProfile={result.businessProfile}
                           location={result.location}/>
    })}

    {rpcLoading && <div style={{color: 'var(--gray)'}}>refreshing results...</div>}
  </div>;
}

